/**
 * InvitationFilmPage — /film/:token
 *
 * Cinematic flow triggered by "View Official Invitation":
 *  1. Orient video autoplays immediately (loop, unmuted)
 *  2. Device orientation is monitored continuously
 *  3. Landscape → large gold Play button appears (instantly, no delay)
 *  4. Play tap → stop orient video → start main film
 *  5. Main film ends → navigate to existing /experience/:token
 *
 * Strict rules:
 *  - Main film does NOT autoplay (only starts on explicit Play tap)
 *  - Play button ONLY visible in landscape
 *  - No headers, no menus, no extra text (except optional rotation hint)
 *  - No intermediate screens after film ends
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';

const API = process.env.REACT_APP_BACKEND_URL;

/* Orient video — user-supplied CDN asset */
const ORIENT_VIDEO =
  'https://customer-assets.emergentagent.com/job_625973e7-a41d-4144-a1a0-77cca3082b09/artifacts/h1uqnrhj_Rotate%20video%20-%20720WebShareName.mp4';

/* Official invitation film — hardcoded production-safe URL */
const VIDEO_URL =
  'https://customer-assets.emergentagent.com/job_96f73dc0-b292-41dd-a366-8b41de2ebdcc/artifacts/sj38t5q9_4.9.26%20Invitation%20Video.mp4';

const SILK_BG =
  'https://customer-assets.emergentagent.com/job_b4805a07-765d-4b82-bd36-89754495be40/artifacts/wmrlspr0_new%20background%20for%20site%202.png';

export default function InvitationFilmPage() {
  const { token } = useParams();
  const navigate  = useNavigate();

  const orientRef    = useRef(null);
  const filmRef      = useRef(null);
  const containerRef = useRef(null);

  const [filmUrl, setFilmUrl]             = useState('');
  const [phase, setPhase]                 = useState('orient'); // 'orient' | 'film' | 'rotate-back'
  const [isLandscape, setIsLandscape]     = useState(
    () => window.innerWidth > window.innerHeight
  );
  const [autoplayBlocked, setAutoplayBlocked] = useState(false);

  /* ── Fetch main film URL from config ─────────────────────────────────── */
  useEffect(() => {
    fetch(`${API}/api/public/guest-site-config`)
      .then(r => r.json())
      .then(d => {
        const raw = d.video_url || '';
        setFilmUrl(raw.startsWith('http') ? raw : `${API}${raw}`);
      })
      .catch(() => {});
  }, []);

  /* ── Real-time orientation detection ──────────────────────────────────── */
  useEffect(() => {
    const check = () =>
      setIsLandscape(window.innerWidth > window.innerHeight);
    window.addEventListener('resize', check);
    window.addEventListener('orientationchange', check);
    return () => {
      window.removeEventListener('resize', check);
      window.removeEventListener('orientationchange', check);
    };
  }, []);

  /* ── Autoplay orient video on mount ───────────────────────────────────── */
  useEffect(() => {
    const v = orientRef.current;
    if (!v) return;

    const tryPlay = () => {
      v.muted = false;
      v.play().catch(() => {
        // Fallback 1: muted autoplay (required by iOS Safari)
        v.muted = true;
        v.play().catch(() => {
          // Fallback 2: all autoplay blocked — show tap overlay
          setAutoplayBlocked(true);
        });
      });
    };

    // Force preload, then attempt play
    v.load();
    tryPlay();
    // Also retry once data is available (race with load)
    v.addEventListener('canplay', tryPlay, { once: true });
    return () => v.removeEventListener('canplay', tryPlay);
  }, []);

  /* ── Tap-to-start: user gesture unlocks autoplay ──────────────────────── */
  const handleTapToStart = useCallback(() => {
    setAutoplayBlocked(false);
    const v = orientRef.current;
    if (!v) return;
    v.muted = false;
    v.play().catch(() => {
      v.muted = true;
      v.play().catch(() => {});
    });
  }, []);

  /* ── Helper: exit fullscreen — covers all browsers incl. Samsung Internet ── */
  const exitFs = useCallback(() => {
    try {
      if (document.fullscreenElement && document.exitFullscreen) {
        document.exitFullscreen().catch(() => {});
      } else if (document.webkitFullscreenElement && document.webkitExitFullscreen) {
        // Samsung Internet 4-9.2 / Safari
        document.webkitExitFullscreen();
      } else if (document.mozFullScreenElement && document.mozCancelFullScreen) {
        document.mozCancelFullScreen();
      }
    } catch (_) {}
  }, []);

  /* ── Play tap: stop orient, go fullscreen, start film ────────────────── */
  const handlePlay = useCallback(() => {
    if (orientRef.current) {
      orientRef.current.pause();
    }
    setPhase('film');

    const v = filmRef.current;
    if (!v) return;

    // Start playback (within user-gesture context)
    v.play().catch(err => console.warn('Film play() failed:', err));

    // Request fullscreen — full cross-browser chain:
    // 1. Standard on container   (Chrome, Samsung Internet 10.1+, Firefox, Edge)
    // 2. Webkit prefix on container (Samsung Internet 4-9.2, older Safari)
    // 3. Standard on video element  (Samsung Internet fallback if container blocked)
    // 4. iOS native video fullscreen (iOS Safari)
    const el = containerRef.current;
    if (el?.requestFullscreen) {
      el.requestFullscreen().catch(() => {
        // Container blocked — try video element directly (Samsung Internet fallback)
        if (v.requestFullscreen) {
          v.requestFullscreen().catch(() => {
            if (v.webkitEnterFullscreen) v.webkitEnterFullscreen();
          });
        } else if (v.webkitEnterFullscreen) {
          v.webkitEnterFullscreen();
        }
      });
    } else if (el?.webkitRequestFullscreen) {
      // Samsung Internet 4-9.2 / older Safari
      el.webkitRequestFullscreen();
    } else if (v.webkitEnterFullscreen) {
      // iOS Safari native video fullscreen
      v.webkitEnterFullscreen();
    }
  }, []);

  /* ── Volume envelope: fade-in 0→4s, fade-out 2:02→2:07, nav at 2:07 ─── */
  const handleTimeUpdate = useCallback(() => {
    const v = filmRef.current;
    if (!v) return;
    const t = v.currentTime;

    // Fade in: first 4 seconds (0 → full volume)
    if (t < 4) {
      v.volume = t / 4;
      return;
    }

    // Fade out: 2:02 (122s) → 2:07 (127s)
    if (t >= 122) {
      v.volume = Math.max(0, 1 - (t - 122) / 5);
    } else if (v.volume < 1) {
      v.volume = 1;
    }

    // Hard cut-off at 2:07 in case onEnded doesn't fire (e.g. longer source)
    if (t >= 127) {
      exitFs();
      setPhase('rotate-back');
    }
  }, [exitFs, navigate, token]);

  /* ── Rotate-back phase: navigate when portrait detected (or 4s fallback) ─ */
  useEffect(() => {
    if (phase !== 'rotate-back') return;
    // If already portrait, navigate after a brief moment for the screen to render
    if (!isLandscape) {
      const t = setTimeout(() => navigate(`/?token=${token}`), 600);
      return () => clearTimeout(t);
    }
    // Fallback: navigate after 4s even if user doesn't rotate
    const fallback = setTimeout(() => navigate(`/?token=${token}`), 4000);
    return () => clearTimeout(fallback);
  }, [phase, isLandscape, navigate, token]);

  /* ── Film ends → exit fullscreen, show rotate-back prompt ────────────── */
  const handleFilmEnded = useCallback(() => {
    exitFs();
    setPhase('rotate-back');
  }, [exitFs]);

  /* ── Shared container styles ─────────────────────────────────────────── */
  const fullscreen = {
    position: 'fixed',
    inset: 0,
    background: '#000',
    overflow: 'hidden',
  };

  return (
    <div ref={containerRef} style={fullscreen}>

      {/* Dimmed silk background — cinematic depth */}
      <div style={{
        position: 'absolute', inset: 0, zIndex: 0,
        backgroundImage: `url('${SILK_BG}')`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        opacity: 0.10,
      }} />
      <div style={{
        position: 'absolute', inset: 0, zIndex: 1,
        background: 'rgba(0,0,0,0.84)',
      }} />

      {/* ── Orient video — always in DOM so it preloads; hidden via opacity when film plays */}
      <video
        ref={orientRef}
        src={ORIENT_VIDEO}
        loop
        playsInline
        autoPlay
        preload="auto"
        data-testid="orient-video"
        style={{
          position: 'absolute', inset: 0,
          width: '100%', height: '100%',
          objectFit: 'contain',
          zIndex: phase === 'orient' ? 2 : -1,
          opacity: phase === 'orient' ? 1 : 0,
          pointerEvents: 'none',
          transition: 'opacity 0.3s ease',
        }}
      />

      {/* ── Main film — always in DOM with preload="auto"; hidden via opacity until Play tap */}
      {filmUrl && (
        <video
          ref={filmRef}
          src={VIDEO_URL}
          playsInline
          preload="auto"
          onEnded={handleFilmEnded}
          onTimeUpdate={handleTimeUpdate}
          data-testid="main-film-video"
          style={{
            position: 'absolute', inset: 0,
            width: '100%', height: '100%',
            objectFit: 'contain',
            background: '#000',
            zIndex: phase === 'film' ? 3 : -1,
            opacity: phase === 'film' ? 1 : 0,
            pointerEvents: phase === 'film' ? 'auto' : 'none',
            transition: 'opacity 0.3s ease',
          }}
        />
      )}

      {/* ── Play button — ONLY shown in landscape, during orient phase ───── */}
      {phase === 'orient' && isLandscape && (
        <div
          data-testid="film-play-button"
          onClick={handlePlay}
          style={{
            position: 'absolute', inset: 0, zIndex: 10,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
          }}
        >
          {/* Gold ring */}
          <div style={{
            width: 124,
            height: 124,
            borderRadius: '50%',
            border: '2px solid rgba(212,168,83,0.88)',
            background: 'rgba(0,0,0,0.50)',
            backdropFilter: 'blur(8px)',
            WebkitBackdropFilter: 'blur(8px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: [
              '0 0 40px rgba(212,168,83,0.35)',
              '0 0 100px rgba(212,168,83,0.15)',
              'inset 0 1.5px 0 rgba(255,235,130,0.30)',
            ].join(', '),
            marginBottom: 18,
            transition: 'transform 0.15s ease, box-shadow 0.15s ease',
          }}>
            {/* Play triangle */}
            <div style={{
              width: 0, height: 0,
              borderTop:    '24px solid transparent',
              borderBottom: '24px solid transparent',
              borderLeft:   '44px solid rgba(212,168,83,0.92)',
              marginLeft: 10,
              filter: 'drop-shadow(0 0 10px rgba(212,168,83,0.55))',
            }} />
          </div>

          <p style={{
            fontFamily: "'Cinzel', serif",
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: '4px',
            textTransform: 'uppercase',
            color: 'rgba(212,168,83,0.85)',
            margin: 0,
          }}>
            Play
          </p>
        </div>
      )}

      {/* ── Tap-to-start fallback — shown ONLY when autoplay is blocked ──── */}
      {autoplayBlocked && phase === 'orient' && (
        <div
          data-testid="tap-to-start"
          onClick={handleTapToStart}
          style={{
            position: 'absolute', inset: 0, zIndex: 9,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            cursor: 'pointer',
          }}
        >
          <p style={{
            fontFamily: "'Cinzel', serif",
            fontSize: 12,
            fontWeight: 600,
            letterSpacing: '3px',
            textTransform: 'uppercase',
            color: 'rgba(212,168,83,0.80)',
            margin: 0,
          }}>
            Tap to Start
          </p>
        </div>
      )}

      {/* ── Rotate-back screen — position:fixed overlays everything after film ends ── */}
      {phase === 'rotate-back' && (
        <>
          <style>{`
            @keyframes cv80-phone-rotate {
              0%,  20% { transform: rotate(90deg); }
              60%, 80% { transform: rotate(0deg);  }
              100%      { transform: rotate(90deg); }
            }
            @keyframes cv80-arrow-pulse {
              0%, 100% { opacity: 0.4; }
              50%       { opacity: 1;   }
            }
          `}</style>
          <div style={{
            position: 'fixed', inset: 0, zIndex: 50,
            background: 'rgba(0,0,0,0.96)',
            display: 'flex', flexDirection: 'column',
            alignItems: 'center', justifyContent: 'center',
            gap: 28,
          }}>
            {/* Phone icon animating from landscape → portrait */}
            <div style={{ animation: 'cv80-phone-rotate 2.4s ease-in-out infinite' }}>
              <svg viewBox="0 0 52 90" width="44" height="76" fill="none"
                   xmlns="http://www.w3.org/2000/svg">
                <rect x="2" y="2" width="48" height="86" rx="7"
                      stroke="rgba(212,168,83,0.85)" strokeWidth="3"/>
                <rect x="18" y="8" width="16" height="3" rx="1.5"
                      fill="rgba(212,168,83,0.7)"/>
                <circle cx="26" cy="81" r="3.5"
                        fill="rgba(212,168,83,0.7)"/>
              </svg>
            </div>

            {/* Pulsing rotation arrow */}
            <div style={{ animation: 'cv80-arrow-pulse 1.2s ease-in-out infinite' }}>
              <svg viewBox="0 0 40 40" width="36" height="36" fill="none"
                   xmlns="http://www.w3.org/2000/svg">
                <path d="M8 20 A14 14 0 0 1 32 20"
                      stroke="rgba(212,168,83,0.80)" strokeWidth="2.5" strokeLinecap="round"/>
                <polyline points="28,13 32,20 24,21"
                          stroke="rgba(212,168,83,0.80)" strokeWidth="2.5"
                          strokeLinecap="round" strokeLinejoin="round" fill="none"/>
              </svg>
            </div>

            <p style={{
              fontFamily: "'Cinzel', serif",
              fontSize: 11, fontWeight: 700,
              letterSpacing: '4px', textTransform: 'uppercase',
              color: 'rgba(212,168,83,0.80)',
              margin: 0, textAlign: 'center',
            }}>
              Rotate to continue
            </p>
          </div>
        </>
      )}

    </div>
  );
}
